import { html } from 'lit';

import { ViewBase } from '../view-base.js';
import { tutorialViewStyles } from './tutorial-view-styles.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '../../components/zero-md/zero-md.js';

import { Q1SpinnerPanel } from '../../components/q1-spinner-panel/q1-spinner-panel.js';

export class TutorialView extends ViewBase {
  static styles = [
    ...super.styles,
    tutorialViewStyles,
  ];

  static properties = {
    ...super.properties,

    tutorialKey: { type: String },
    tutorial: { type: Object },
    
    loading: { type: Boolean },
    isError: { type: Boolean },
    errorMessage: { type: String },

    tutorialManager: { type: Object },
  };

  constructor() {
    super();
    this.tutorialKey = null;
    this.tutorial = null;
    
    this.loading = true;
    this.isError = false;
    this.errorMessage = null;

    this.tutorialManager = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this._initViewFromRouteParams();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('routeParams') || changedProperties.has('currentMember')) {
      this._initViewFromRouteParams();
    }
  }

  _initViewFromRouteParams() {
    this.loading = true;
    this.isError = false;
    this.errorMessage = '';

    this.tutorialKey = this.routeParams?.key?.trim();
    
    this.tutorialManager = document.querySelector('q1-home').tutorialManager;
    this.tutorialManager.loadingComplete.then(() => {
      this.tutorial = this.tutorialManager.allTutorialsByKey[this.tutorialKey];

      if (!this.tutorial) {
        this.errorMessage = `Invalid tutorial key: ${this.tutorialKey || '[BLANK]'}`;
        this.isError = true;
      } else {
        this.tutorialManager.trackTutorialView(this.tutorialKey)
          .catch((error) => { console.log('Error tracking tutorial view: ', error); });
      }

      this.loading = false;
    }).catch((error) => {
      console.log('Error loading tutorials:', error);
      this.loading = false;
    });
  }

  markCompleted() {
    this.loading = true;

    this.tutorialManager
      .markTutorialCompleted(this.tutorialKey).then(() => {
        // Tutorial Manager has already updated the tutorial, we just need to re-fire the lit 
        // setter so that the view is refreshed.
        this.tutorial = { ...this.tutorial };

        this.appElement.refreshCurrentMember(); // update the app header
        
        this.showToast('Tutorial marked as completed.', null, 'success');

        this.loading = false;
      }).catch((error) => {
        console.log('Error marking tutorial complete: ', error);
        this.showToast(error.message, 'Error Marking Tutorial Completed.', 'danger');
        this.loading = false;
      });
  }

  get contentTemplate() {
    if (this.isError) return html`
      <sl-button variant="text" size="large" href="/tutorials">
        <sl-icon library="material" name="arrow_back" slot="prefix"></sl-icon>
        Back to All Tutorials
      </sl-button>

      <h1>q1 Network Member Tutorial</h1>

      <sl-alert variant="danger" open>
        <sl-icon library="material" name="report" slot="icon"></sl-icon>
        <strong>Error loading tutorial.</strong><br/>
        ${this.errorMessage}
      </sl-alert>
    `;

    return html`
      <sl-button variant="text" size="large" href="/tutorials">
        <sl-icon library="material" name="arrow_back" slot="prefix"></sl-icon>
        Back to All Tutorials
      </sl-button>

      ${!this.tutorial ? null : html`
        <header-bar>
          <h1>
            <span class="prefix">Member Tutorial:</span>
            ${this.tutorial.title}
          </h1>

          ${this.tutorial?.isCompleted ? html`
            <div class="info">
              <sl-icon library="material" name="task_alt"></sl-icon>  
              <span>Completed</span>
              <sl-format-date
                date=${this.tutorial.completedAt}
                month="short" day="numeric" year="numeric"
              ></sl-format-date>
            </div>
          ` : html`
            <sl-button
              variant="success"
              size="large"
              @click=${this.markCompleted}
            >
              <sl-icon library="material" name="task_alt"></sl-icon>  
              Mark Completed
            </sl-button>
          `}
        </header-bar>

        ${!this.tutorial.isUpdatedSinceCompletion ? null : html`
          <!-- Member has completed a different version -->
          <sl-alert variant="primary" open>
            <sl-icon library="material" name="info" slot="icon"></sl-icon>
            
            <strong>Tutorial has been updated since you completed it.</strong><br/>
            
            You completed version ${this.tutorial.completedVersion} on 
            ${this.tutorial.completedAtString}. 
            
            Version ${this.tutorial.version} was published on 
            ${this.tutorial.versionPublishDateString}. 
          </sl-alert>
        `}

        <zero-md>
          <script type="text/markdown">${this.tutorial?.content}</script>

          <template data-append>
            <style>
              .markdown-body {
                background: white;
                color: var(--gray-9);
                padding: 1.5rem;
              }
              @media(min-width: 650px) {
                .markdown-body {
                  padding: 2rem;
                }
              }
            </style>
          </template>
        </zero-md>
      `}
      
      <q1-spinner-panel label="Processing..." ?open=${this.loading}></q1-spinner-panel>
    `;
  }
}

customElements.define('tutorial-view', TutorialView);